import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import {
	ColorPicker,
	ColorPickerArea,
	ColorPickerChannelSlider,
	ColorPickerEyeDropperTrigger,
	ColorPickerLabel,
	ColorPickerPresetSwatches,
	ColorPickerValueSwatch,
	parseColor,
	type ValueChangeDetails,
} from "../../components/ui/color-picker.js";
import {
	HUB_TAG_COLOR_IDS,
	hubColorHex,
	hubToneSolidHex,
	type HubTagColorId,
} from "./hub-task-colors.js";

const HUB_PRESET_HEX = HUB_TAG_COLOR_IDS.map((id) => hubToneSolidHex(id));
const POPOVER_WIDTH = 272;
const POPOVER_PAD = 12;

function hexFromPicker(details: ValueChangeDetails): string {
	try {
		return details.value.toString("hex").toLowerCase();
	} catch {
		return details.valueAsString.toLowerCase();
	}
}

function parseUserHex(raw: string): string | undefined {
	const digits = raw.trim().replace(/^#/, "");
	if (/^[0-9a-fA-F]{3}$/.test(digits)) {
		return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`.toLowerCase();
	}
	if (/^[0-9a-fA-F]{6}$/.test(digits)) return `#${digits.toLowerCase()}`;
	return undefined;
}

function placePopover(anchor: DOMRect, height: number): { top: number; left: number } {
	let left = anchor.left;
	if (left + POPOVER_WIDTH > window.innerWidth - POPOVER_PAD) {
		left = window.innerWidth - POPOVER_WIDTH - POPOVER_PAD;
	}
	if (left < POPOVER_PAD) left = POPOVER_PAD;
	let top = anchor.bottom + 8;
	if (top + height > window.innerHeight - POPOVER_PAD) {
		top = Math.max(POPOVER_PAD, anchor.top - height - 8);
	}
	return { top, left };
}

export function HubColorPalette({
	value,
	onChange,
	heading,
	ariaLabel,
	disabled,
	compact,
}: {
	value: HubTagColorId | string;
	onChange: (color: string) => void;
	heading?: string;
	ariaLabel: string;
	disabled?: boolean;
	compact?: boolean;
}) {
	const hexFieldId = useId();
	const initialHex = hubColorHex(value, ariaLabel);
	const [draft, setDraft] = useState(() => parseColor(initialHex));
	const [hexText, setHexText] = useState(initialHex.toUpperCase());

	const commitHex = (hex: string) => {
		setDraft(parseColor(hex));
		setHexText(hex.toUpperCase());
		onChange(hex);
	};

	const handleValueChange = (details: ValueChangeDetails) => {
		setDraft(details.value);
		setHexText(hexFromPicker(details).toUpperCase());
	};

	const handleValueChangeEnd = (details: ValueChangeDetails) => {
		const hex = hexFromPicker(details);
		setDraft(details.value);
		setHexText(hex.toUpperCase());
		onChange(hex);
	};

	const applyTypedHex = () => {
		const next = parseUserHex(hexText);
		if (next) commitHex(next);
		else setHexText(draft.toString("hex").toUpperCase());
	};

	return (
		<div className={compact ? "hub-color-palette-wrap is-compact" : "hub-color-palette-wrap"}>
			{heading ? <p className="hub-color-palette-heading">{heading}</p> : null}
			<ColorPicker
				inline
				closeOnSelect={false}
				className="hub-tone-color-picker max-w-none gap-2.5"
				value={draft}
				disabled={disabled}
				onValueChange={handleValueChange}
				onValueChangeEnd={handleValueChangeEnd}
			>
				<ColorPickerLabel className="sr-only">{ariaLabel}</ColorPickerLabel>
				<ColorPickerArea className="hub-tone-color-area" />
				<div className="hub-tone-color-tools">
					<ColorPickerEyeDropperTrigger
						className="hub-tone-color-eyedropper"
						aria-label="Pick a colour from the screen"
					/>
					<ColorPickerChannelSlider channel="hue" className="hub-tone-color-hue" />
				</div>
				<div className="hub-tone-color-hex-row">
					<div className="hub-tone-color-hex-preview" aria-hidden="true">
						<ColorPickerValueSwatch className="hub-tone-color-hex-swatch" />
					</div>
					<span className="hub-tone-color-hex-label" id={`${hexFieldId}-label`}>Hex</span>
					<input
						id={hexFieldId}
						className="hub-tone-color-hex-input"
						name="hub-tag-hex"
						type="text"
						spellCheck={false}
						autoComplete="off"
						autoCapitalize="characters"
						inputMode="text"
						maxLength={7}
						disabled={disabled}
						aria-labelledby={`${hexFieldId}-label`}
						value={hexText}
						onChange={(event) => {
							const digits = event.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
							setHexText(`#${digits.toUpperCase()}`);
							const parsed = parseUserHex(digits);
							if (parsed) {
								setDraft(parseColor(parsed));
								if (digits.length === 6) onChange(parsed);
							}
						}}
						onBlur={applyTypedHex}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								applyTypedHex();
							}
						}}
					/>
				</div>
				<ColorPickerPresetSwatches
					colors={HUB_PRESET_HEX}
					className="gap-1.5"
					swatchClassName={compact ? "size-[18px]" : "size-5"}
				/>
			</ColorPicker>
		</div>
	);
}

export function HubColorPopover({
	children,
	ariaLabel,
	onDismiss,
}: {
	children: ReactNode;
	ariaLabel: string;
	onDismiss?: () => void;
}) {
	const ghostRef = useRef<HTMLSpanElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [host, setHost] = useState<HTMLElement | null>(null);
	const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

	useLayoutEffect(() => {
		const dialog = ghostRef.current?.closest("dialog");
		const nextHost = dialog instanceof HTMLElement ? dialog : document.body;
		nextHost.classList.add("has-color-picker");
		setHost(nextHost);
		const update = () => {
			const parent = ghostRef.current?.closest(".task-tag-pick-item, .task-assignees-option, .task-field") as HTMLElement | null;
			const anchor = (parent?.querySelector(".task-tag-color-trigger, .task-select-color-name, .task-assignees-color-name") as HTMLElement | null)
				?? parent;
			if (!anchor) return;
			const height = panelRef.current?.offsetHeight ?? 340;
			setCoords(placePopover(anchor.getBoundingClientRect(), height));
		};
		update();
		const frame = window.requestAnimationFrame(update);
		window.addEventListener("resize", update);
		document.addEventListener("scroll", update, true);
		return () => {
			nextHost.classList.remove("has-color-picker");
			window.cancelAnimationFrame(frame);
			window.removeEventListener("resize", update);
			document.removeEventListener("scroll", update, true);
		};
	}, []);

	useEffect(() => {
		if (!onDismiss) return;
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (panelRef.current?.contains(target)) return;
			if (ghostRef.current?.closest(".task-tag-pick-item, .task-assignees-option, .task-field")?.contains(target)) return;
			onDismiss();
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onDismiss();
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [onDismiss]);

	return (
		<>
			<span ref={ghostRef} className="hub-color-popover-anchor" aria-hidden="true" />
			{host
				? createPortal(
					<div
						ref={panelRef}
						className="hub-color-popover is-fixed"
						role="dialog"
						aria-label={ariaLabel}
						style={coords ? { top: coords.top, left: coords.left } : { visibility: "hidden" }}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onPointerDown={(event) => event.stopPropagation()}
					>
						{children}
					</div>,
					host,
				)
				: null}
		</>
	);
}
